import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@apollo/client/react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { Button } from "@/components/design/Button";
import { personDisplayName, personDisplayRole } from "@/lib/person-display";
import { MatchResultDetailDocument, MatchResultListDocument } from "@/graphql/generated/operations";

// Die Ergebnisse eines Matches, aufgeklappt unter seiner Zeile, und daraus
// einzelne Menschen ins Projekt holen.
//
// Zwei Stufen, weil ein Match mehrere Ergebnisse hat und ein Ergebnis mehrere
// Teams: erst die Liste der Durchläufe (wann, wie viele Kandidaten), dann auf
// Klick die Menschen darin. Alles auf einmal zu laden hiesse, für jede Zeile
// den vollen Teamaufbau zu holen, den fast niemand aufklappt.
//
// Übernommen wird die PERSON, nicht das Team: ein Match schlägt eine Aufstellung
// vor, wer davon ins Projekt geht, entscheidet der Mensch. Sie landet als
// Gefragte, nicht als Mitglied - dieselbe Stufe wie jemand aus der Community.
//
// Eine schlichte Personenliste, KEIN Ranking-Panel. Hier wird ausgewählt, nicht
// bewertet: die Frage ist "wen nehme ich mit", und darauf antwortet eine Zeile
// mit Name, Beruf und einem Knopf. Die bewertende Ansicht mit Bändern und
// Scores stand kurz an dieser Stelle und war der falsche Weg - sie beantwortet
// eine Frage, die man in MATCH stellt, und drängte den Knopf, um den es hier
// geht, an den Rand. Wer die Bewertung sehen will, geht über "Ansehen" hin.

export function ProjectMatchResults({
  matchId,
  candidates,
  onToggleCandidate,
  onClose,
  matchHref,
}: {
  matchId: string;
  candidates: readonly string[];
  onToggleCandidate: (personId: string) => void;
  /** Schliesst die ganze Fläche. Fehlt, wenn der Aufrufer das selbst regelt. */
  onClose?: () => void;
  /** Weg zum Match. Fehlt, wenn es von hier keinen geben soll. */
  matchHref?: string;
}) {
  const intl = useIntl();
  const [openResultId, setOpenResultId] = useState<string | null>(null);

  const results = useQuery(MatchResultListDocument, {
    variables: { input: { first: 20, where: { matchId: { eq: matchId } } } },
  });
  const nodes = results.data?.matchResults?.nodes ?? [];

  const detail = useQuery(MatchResultDetailDocument, {
    variables: { id: openResultId ?? "" },
    skip: !openResultId,
  });
  // Ein Mensch kann in mehreren Teams desselben Ergebnisses stehen; doppelte
  // Zeilen wären eine Aussage über die Aufstellung, nicht über die Person.
  const persons = [
    new Map(
      (detail.data?.matchResult?.teams ?? [])
        .flatMap((t) => t?.persons ?? [])
        .flatMap((p) => (p ? [[p.id, p] as const] : [])),
    ).values(),
  ].flatMap((it) => [...it]);
  // Das erste Team trägt die Bewertung. Bei einer Rangliste ist es das einzige,
  // bei einer Aufstellung das, was der Solver vorschlägt.
  if (results.loading && nodes.length === 0) {
    return (
      <p className="smycles-field-meta text-ink-muted">
        <FormattedMessage id="project.results.loading" defaultMessage="Ergebnisse werden geladen …" />
      </p>
    );
  }

  if (nodes.length === 0) {
    return (
      <p className="smycles-field-meta text-ink-muted">
        <FormattedMessage id="project.results.none" defaultMessage="Dieses Match hat noch kein Ergebnis." />
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {/* Die Fläche trägt ihre eigene Kopfzeile, der Rahmen darum keine zweite.
          Vorher stand der Matchname doppelt da, mit zwei "Schliessen" daneben,
          von denen eines die Fläche und eines nur die Personenliste zuklappte. */}
      {onClose || matchHref ? (
        <div className="flex items-center justify-end gap-3">
          {matchHref ? (
            <Link to={matchHref} className="smycles-field-meta text-ink-muted transition hover:text-ink">
              <FormattedMessage id="project.candidates.openMatch" defaultMessage="Ansehen" />
            </Link>
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="smycles-field-meta text-ink transition hover:text-ink"
            >
              <FormattedMessage id="project.results.close" defaultMessage="Schliessen" />
            </button>
          ) : null}
        </div>
      ) : null}
      {nodes.map((result) => {
        const open = openResultId === result.id;
        return (
          <div key={result.id}>
            <button
              id={`project-results-item-${result.id}`}
              type="button"
              onClick={() => setOpenResultId(open ? null : result.id)}
              className="flex w-full flex-wrap items-baseline gap-2 text-left"
            >
              <span className="smycles-field-value">
                {result.name ||
                  intl.formatMessage({ id: "project.results.unnamed", defaultMessage: "Ergebnis" })}
              </span>
              <span className="smycles-field-meta text-ink-muted">
                {result.createdAt ? (
                  <FormattedDate value={result.createdAt} day="2-digit" month="2-digit" year="numeric" />
                ) : null}
                {" · "}
                <FormattedMessage
                  id="project.results.candidates"
                  defaultMessage="{count, plural, one {1 Kandidat} other {# Kandidaten}}"
                  values={{ count: result.candidateCount ?? 0 }}
                />
              </span>
              <span className="smycles-field-meta ml-auto text-ink-muted">
                {open ? (
                  <FormattedMessage id="project.results.hide" defaultMessage="Personen verbergen" />
                ) : (
                  <FormattedMessage id="project.results.show" defaultMessage="Personen zeigen" />
                )}
              </span>
            </button>

            {open ? (
              <div className="mt-2 space-y-2">
                {detail.loading ? (
                  <p className="smycles-field-meta text-ink-muted">
                    <FormattedMessage id="project.results.loading" defaultMessage="Ergebnisse werden geladen …" />
                  </p>
                ) : null}
                {persons.map((person) => {
                  const picked = candidates.includes(person.id);
                  return (
                    <PersonResultRow
                      key={person.id}
                      person={{
                        id: person.id,
                        name: personDisplayName(person),
                        avatarContent: person.avatar?.content,
                        avatarMime: person.avatar?.mimeType,
                      }}
                      title={personDisplayName(person)}
                      sub={personDisplayRole(person)}
                      // Wer aus einem Ergebnis jemanden übernimmt, entscheidet
                      // über einen Menschen, den er nicht kennt. Arbeitgeber und
                      // Ort sind das Wenige, was jede Person trägt und was die
                      // Entscheidung trägt - alles Weitere steht im Profil, das
                      // die Zeile verlinkt.
                      description={[person.currentEmployer, person.city].filter(Boolean).join(" · ") || undefined}
                      embedded
                      actions={
                        <Button
                          id={`project-results-pick-${result.id}-${person.id}`}
                          variant={picked ? "primary" : "secondary"}
                          size="sm"
                          onClick={() => onToggleCandidate(person.id)}
                        >
                          {picked ? (
                            <FormattedMessage id="project.candidates.picked" defaultMessage="Hinzugefügt" />
                          ) : (
                            <FormattedMessage id="project.candidates.pick" defaultMessage="Hinzufügen" />
                          )}
                        </Button>
                      }
                    />
                  );
                })}
                {!detail.loading && persons.length === 0 ? (
                  <p className="smycles-field-meta text-ink-muted">
                    <FormattedMessage
                      id="project.results.noPersons"
                      defaultMessage="In diesem Ergebnis steht niemand."
                    />
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
